<template>
  <!-- Row: Chart -->
  <div
    class="row f-row"
    :style="{
      height: !isAdvanced
        ? '100%'
        : controlsSpace
        ? 'calc(100% - 36px)!important'
        : 'calc(100% - 22px)!important',
    }"
  >
    <div class="col col-12 text-center progress-div" v-if="showLoading">
      <QSLoading :isLoading="showLoading" :size="35" :colorTheme="colorTheme" />
    </div>
    <div
      class="col col-12 text-center"
      v-else-if="chartData.length === 0"
      style="position: absolute; left: 0; top: 47.5%;"
    >
      {{ zeroRequests ? "Get data" : "No Data Available" }}
    </div>
    <div class="col col-12" style="height:100%; width:100%">
      <div class="h-100 d-flex flex-column" id="rates-div" style="width: 100%">
        <svg id="svg-rates"></svg>
      </div>
    </div>
  </div>
</template>

<script>
import { D3Chart, isBarrier } from "@/_core/charts/D3Chart";
import { D3HoverPlanning } from "@/_core/charts/D3ChartAnalyticsHover";
import {
  colorCall,
  colorExpo,
  colorHedge,
  colorFwd,
  colorFwdAlpha5,
  colorNet,
  colorNetAlpha2,
  colorNet_2,
  colorPut,
  currencyFormat,
  currencyFormat3,
  darkCall,
  darkFwd,
  darkPut,
  dnCandlesAlpha3,
  formatM,
  formatNumber,
  formatPercent,
  textactiveAlpha2,
  whiteGray,
  whiteGrayAlpha2,
} from "@/_core/_helpers/_parametric_tools";
import { format, select } from "d3";
import { idxOfDate } from "@/_core/data/Advanced";
import QSLoading from "@/components/QSLoading.vue";

export default {
  name: "RatesChart",
  props: {
    adjustTooltip: { required: false, type: Boolean, default: false },
    asset: { required: false, default: "currencies" },
    averages: { required: true, default: () => [] },
    budgetRate: { required: true },
    candleData: { required: true, default: () => [] },
    chartData: { required: true, default: () => [] },
    colorTheme: { required: true, type: Boolean, default: true },
    controlsSpace: { required: false, type: Boolean, default: true },
    createAnalyticsVars: { required: false },
    currency: { required: true },
    dateFormat: { required: true },
    derivativeAverages: { required: true, default: () => [] },
    derivativeDateField: { required: false, default: "date" },
    hedgeData: { required: true, default: () => [] },
    historicalData: { required: false, default: () => [] },
    includeFutures: { required: false, type: Boolean, default: true },
    includeBasis: { required: false, type: Boolean, default: true },
    includeRatio: { required: false, type: Boolean, default: true },
    includeOtherCosts: { required: false, type: Boolean, default: true },
    isFuture: { required: true },
    minMaxYAxis: { required: true },
    pickedBucket: { required: false, default: "1M" },
    processData: { required: false },
    renderChartSignal: { required: true },
    showAve: { required: true },
    showCommited: { required: false, type: Boolean, default: false },
    showExpo: { required: true },
    showFixedHedged: { required: false, type: Boolean, default: false },
    showHedge: { required: true },
    showLCall: { required: true },
    showLFwd: { required: true },
    showLoading: { required: true, type: Boolean, default: false },
    showLPut: { required: true },
    showMkt: { required: true },
    showNet: { required: true },
    showProtected: { required: false, type: Boolean, default: false },
    showSCall: { required: true },
    showSFwd: { required: true },
    showSimulations: { required: true, default: () => [] },
    showSPut: { required: true },
    showTooltip: { required: true },
    showTooltipDeriv: { required: true },
    simulations: { required: true, default: () => [] },
    zeroRequests: { required: true, type: Boolean, default: true },
    isZoomY: { required: false, default: false },
    isAdvanced: { required: false, default: true },
  },
  components: { QSLoading },
  data() {
    return {
      chartBodyScatter: null,
      chartBodyTA: null,
      chartName: "chart1",
      currencyFormat: currencyFormat3,
      currencyFormat1: format(currencyFormat),
      D3Chart: null,
      D3HoverPlanning: null,
      derivativeAveragesFiltered: [],
      derivativeSize: 14,
      fNames: [],
      formatVars: {
        userHovered: {
          date: "",
          variable: "",
          hovered: false,
          fromChart: false,
        },
      },
      isPrices: true,
      lCall: require("@/assets/images/lCall.svg"),
      lCallOff: require("@/assets/images/lCallA.svg"),
      lCallOn: require("@/assets/images/lCallH.svg"),
      lFwd: require("@/assets/images/lFwd.svg"),
      lFwdOff: require("@/assets/images/lFwdA.svg"),
      lFwdOn: require("@/assets/images/lFwdH.svg"),
      logo: require("@/assets/images/symbol_blue.svg"),
      lPut: require("@/assets/images/lPut.svg"),
      lPutOff: require("@/assets/images/lPutA.svg"),
      lPutOn: require("@/assets/images/lPutH.svg"),
      sCall: require("@/assets/images/sCall.svg"),
      sCallOff: require("@/assets/images/sCallA.svg"),
      sCallOn: require("@/assets/images/sCallH.svg"),
      sFwd: require("@/assets/images/sFwd.svg"),
      sFwdOff: require("@/assets/images/sFwdA.svg"),
      sFwdOn: require("@/assets/images/sFwdH.svg"),
      sPut: require("@/assets/images/sPut.svg"),
      sPutOff: require("@/assets/images/sPutA.svg"),
      sPutOn: require("@/assets/images/sPutH.svg"),
      svgNode: null,
    };
  },
  created() {
    this.D3Chart = new D3Chart("rates-div", "svg-rates");
    this.D3HoverPlanning = new D3HoverPlanning();
    this.fNames = this.isAdvanced
      ? {
          budget: "budget_rate",
          comm: "balance",
          fixed: "hedge_rate",
          fixedHedged: "hedge_rate",
          hedge: "fixed",
          market: "m2m_rate",
          net: "net_rate",
        }
      : {
          budget: "budget",
          comm: "comm",
          fixed: "fixed",
          fixedHedged: "fixedHedged",
          hedge: "hedge",
          market: "market",
          net: "net",
        };
  },
  mounted() {
    this.resizeDerivatives();
    this.selectHighlightImg();
    let _this = this;
    window.addEventListener("resize", function() {
      _this.callWidth();
    });
    this.callWidth();
  },
  watch: {
    colorTheme() {
      this.selectHighlightImg();
      this.callWidth();
    },
    formatVars: {
      handler() {
        setTimeout(() => {
          this.onHoverChart();
        });
      },
      deep: true,
    },
    showLoading(val) {
      if (this.chartData.length > 0 && !val) {
        this.createBarChart();
      } else {
        this.deleteChart();
      }
    },
    chartData() {
      if (this.chartData.length > 0 && !this.showLoading) {
        this.createBarChart();
        this.createDerivativeAverages();
      } else {
        this.deleteChart();
      }
    },
    showExpo() {
      this.callWidth();
    },
    showCommited() {
      this.callWidth();
    },
    showTooltip() {
      this.callWidth();
    },
    showTooltipDeriv() {
      this.callWidth();
    },
    renderChartSignal() {
      if (this.chartData.length > 0) {
        this.createBarChart();
        this.createDerivativeAverages();
      } else {
        this.deleteChart();
      }
    },
  },
  methods: {
    resizeDerivatives() {
      // 1280px = small screen, 1920px = large screen
      const windowWidth = window.innerWidth;
      const minWidth = 1280;
      const maxWidth = 1920;
      const minSize = 14;
      const maxSize = 24;
      if (windowWidth <= minWidth) return minSize;
      if (windowWidth >= maxWidth) return maxSize;
      this.derivativeSize =
        minSize +
        ((windowWidth - minWidth) / (maxWidth - minWidth)) *
          (maxSize - minSize);
    },
    createDerivativeAverages() {
      if (this.derivativeAverages.length > 0) {
        const initDate = this.chartData[0].date;
        const endDate = this.chartData.at(-1).date;

        Object.assign(this, {
          derivativeAveragesFiltered: this.derivativeAverages.filter(
            (d) => d.date >= initDate && d.date <= endDate,
          ),
        });
      }
    },
    selectHighlightImg() {
      if (this.colorTheme) {
        this.lFwdOn = require("@/assets/images/lFwdH.svg");
        this.sFwdOn = require("@/assets/images/sFwdH.svg");
        this.lCallOn = require("@/assets/images/lCallH.svg");
        this.sCallOn = require("@/assets/images/sCallH.svg");
        this.lPutOn = require("@/assets/images/lPutH.svg");
        this.sPutOn = require("@/assets/images/sPutH.svg");
      } else {
        this.lFwdOn = require("@/assets/images/lFwdHB.svg");
        this.sFwdOn = require("@/assets/images/sFwdHB.svg");
        this.lCallOn = require("@/assets/images/lCallHB.svg");
        this.sCallOn = require("@/assets/images/sCallHB.svg");
        this.lPutOn = require("@/assets/images/lPutHB.svg");
        this.sPutOn = require("@/assets/images/sPutHB.svg");
      }
    },
    deleteChart() {
      select("#rates-div")
        .selectAll(".tooltip")
        .remove();
      select("#rates-div")
        .select("svg > *")
        .remove();
      // selectAll(".tooltip").remove();
    },
    validateMinMaxYAxis(minMaxYAxis) {
      let minMaxY = minMaxYAxis;
      if (minMaxY[0] === minMaxY[1]) {
        minMaxY = [0, minMaxY[0] * 2];
      }
      return minMaxY;
    },
    createBarChart() {
      this.deleteChart();
      if (this.chartData.length === 0) {
        return;
      }
      let xRange = this.chartData.map((d) => d.date);
      let localMinMaxYAxis;
      if (this.isAdvanced) {
        localMinMaxYAxis = this.minMaxYAxis;
      } else {
        // === create Analytics data
        let _this = this;
        this.chartData.forEach(function(d) {
          let dat = _this.createAnalyticsVars(
            d,
            _this.includeFutures,
            _this.includeRatio,
            _this.includeBasis,
            _this.includeOtherCosts,
          );
          d[_this.fNames["budget"]] = dat["budget"];
          d[_this.fNames["comm"]] = dat["comm"];
          d[_this.fNames["fixed"]] = dat["fixed"];
          d[_this.fNames["fixedHedged"]] = dat["fixedHedged"];
          d[_this.fNames["hedge"]] = dat["hedge"];
          d[_this.fNames["market"]] = dat["market"];
          d[_this.fNames["net"]] = dat["net"];
        });
        // === auxData and Fields
        let fldForecast = this.fNames["budget"];
        let fldCommited = this.fNames["comm"];
        let fldFixed = this.showFixedHedged
          ? this.fNames["fixed"]
          : this.fNames["fixedHedged"];
        let fldHedged = this.fNames["hedge"];
        let fldMkt = this.fNames["market"];
        let fldNet = this.fNames["net"];
        // === minMax
        localMinMaxYAxis = [Infinity, -Infinity];
        if (this.showExpo) {
          localMinMaxYAxis = this.processData.minMax(
            this.chartData,
            fldForecast,
            fldForecast,
            localMinMaxYAxis,
          );
        }
        if (this.showCommited) {
          localMinMaxYAxis = this.processData.minMax(
            this.chartData,
            fldCommited,
            fldCommited,
            localMinMaxYAxis,
          );
        }
        if (this.showHedge) {
          localMinMaxYAxis = this.processData.minMax(
            this.chartData,
            fldFixed,
            fldFixed,
            localMinMaxYAxis,
          );
        }
        if (this.showProtected) {
          localMinMaxYAxis = this.processData.minMax(
            this.chartData,
            fldHedged,
            fldHedged,
            localMinMaxYAxis,
          );
        }
        if (this.showMkt) {
          localMinMaxYAxis = this.processData.minMax(
            this.chartData,
            fldMkt,
            fldMkt,
            localMinMaxYAxis,
          );
        }
        if (this.showNet) {
          localMinMaxYAxis = this.processData.minMax(
            this.chartData,
            fldNet,
            fldNet,
            localMinMaxYAxis,
          );
        }

        let del = 0.05 * (localMinMaxYAxis[1] - localMinMaxYAxis[0]);
        let del1 = localMinMaxYAxis[0] === 0 ? 0 : del;
        let del2 = localMinMaxYAxis[1] === 0 ? 0 : del;
        localMinMaxYAxis = [
          localMinMaxYAxis[0] - del1,
          localMinMaxYAxis[1] + del2,
        ];
        localMinMaxYAxis = this.validateMinMaxYAxis(localMinMaxYAxis);
      }

      // === set SVG canvas
      this.svgNode = this.D3Chart.setSVG(
        0.5,
        5,
        0.5,
        55,
        0.5,
        20,
        0.5,
        1,
        this.colorTheme,
        this.logo,
        "up",
      );
      if (this.svgNode.height === 0 || this.svgNode.width === 0) {
        return;
      }
      // === set axis
      this.svgNode = this.D3Chart.setAxis(
        this.svgNode,
        this.chartData,
        localMinMaxYAxis,
        this.dateFormat,
        50,
        8,
        false,
        [],
        true,
        true,
        false,
        this.currencyFormat,
        false,
      );
      // ==== zoom setup
      this.chartBodyTA = this.svgNode
        .append("g")
        .attr("class", "chartBodyTA")
        .attr("clip-path", `url(#clipTA${this.chartName})`);
      this.svgNode
        .append("defs")
        .append("clipPath")
        .attr("id", "clipTA" + this.chartName)
        .append("rect")
        .attr("width", this.svgNode.width)
        .attr("height", this.svgNode.height);

      if (this.candleData.length === 0) {
        // const barWidth = 0.7;
        this.D3Chart.backgroundRect(
          this.svgNode,
          this.chartData.map((d) => d.date),
          this.chartData.map((d, i) => i % 2 !== 0),
          "PLAN",
          0,
          0,
          this.chartBodyTA,
        );
      }

      let figuresElemsHover = [];
      if (this.candleData.length > 0) {
        // === alpha candles
        this.svgNode = this.D3Chart.candles(
          this.svgNode,
          this.candleData,
          textactiveAlpha2(this.colorTheme),
          dnCandlesAlpha3(this.colorTheme),
          "TA",
          0.8,
          0.8,
          null,
          null,
          1,
          true,
          xRange.length > 49 ? 0.9 : xRange.length > 19 ? 1 / 5 : 1 / 21,
          xRange,
          false,
          "",
          false,
          "stick",
          "stick2",
          this.chartBodyTA,
        );

        const candleHoverConfiguration = [
          {
            data: this.candleData,
            customNameBars: ["candle", "stick", "stick2"],
          },
        ];

        figuresElemsHover.push({
          type: "candlesOld",
          data: candleHoverConfiguration,
        });
      }

      if (this.showSimulations) {
        if (this.showMkt) {
          this.D3Chart.bars(
            this.svgNode,
            this.simulations,
            0.6,
            "RATEES",
            "rgba(0,0,0,0)",
            whiteGrayAlpha2(this.colorTheme),
            "date",
            "m2m_rate_up",
            true,
            false,
            false,
            2,
            1,
            true,
            "id",
            true,
            "m2m_rate_dn",
            0,
            false,
            0,
            this.chartBodyTA,
          );
        }
        if (this.showHedge) {
          this.D3Chart.bars(
            this.svgNode,
            this.simulations,
            0.4,
            "RATEHS",
            "rgba(0,0,0,0)",
            colorFwdAlpha5(this.colorTheme),
            "date",
            "hedge_rate_up",
            true,
            false,
            false,
            2,
            1,
            true,
            "id",
            true,
            "hedge_rate_dn",
            0,
            false,
            0,
            this.chartBodyTA,
          );
        }
        if (this.showNet) {
          this.D3Chart.bars(
            this.svgNode,
            this.simulations,
            0.2,
            "RATENS",
            colorNet(this.colorTheme),
            colorNetAlpha2(this.colorTheme),
            "date",
            "net_rate_up",
            true,
            false,
            false,
            2,
            0.5,
            true,
            "id",
            true,
            "net_rate_dn",
            0,
            false,
            0,
            this.chartBodyTA,
          );
        }
      }

      if (this.showMkt) {
        this.D3Chart.bars(
          this.svgNode,
          this.chartData,
          0.9,
          "RATEE",
          whiteGray(this.colorTheme),
          "rgba(0,0,0,0)",
          "date",
          this.fNames["market"],
          true,
          false,
          false,
          4,
          2,
          true,
          "date",
          false,
          "",
          0,
          false,
          0,
          this.chartBodyTA,
        );
      }

      if (this.showExpo) {
        this.D3Chart.bars(
          this.svgNode,
          this.chartData,
          0.8,
          "RATEB",
          colorExpo(this.colorTheme),
          "rgba(0,0,0,0)",
          "date",
          this.fNames["budget"],
          true,
          false,
          false,
          4,
          2,
          true,
          "date",
          false,
          "",
          0,
          false,
          0,
          this.chartBodyTA,
        );
      }
      if (this.showCommited) {
        this.D3Chart.bars(
          this.svgNode,
          this.chartData,
          0.7,
          "RATEC",
          colorHedge(this.colorTheme),
          "rgba(0,0,0,0)",
          "date",
          this.fNames["comm"],
          true,
          false,
          false,
          4,
          2,
          true,
          "date",
          false,
          "",
          0,
          false,
          0,
          this.chartBodyTA,
        );
      }

      if (this.showHedge) {
        this.D3Chart.bars(
          this.svgNode,
          this.chartData,
          0.6,
          "RATEH",
          colorFwd,
          "rgba(0,0,0,0)",
          "date",
          this.showFixedHedged
            ? this.fNames["fixed"]
            : this.fNames["fixedHedged"],
          true,
          false,
          false,
          4,
          2,
          true,
          "date",
          false,
          "",
          0,
          false,
          0,
          this.chartBodyTA,
        );
      }

      if (this.showProtected) {
        this.D3Chart.bars(
          this.svgNode,
          this.chartData,
          0.5,
          "RATEP",
          colorNet_2(this.colorTheme),
          "rgba(0,0,0,0)",
          "date",
          this.fNames["hedge"],
          true,
          false,
          false,
          4,
          2,
          true,
          "date",
          false,
          "",
          0,
          false,
          0,
          this.chartBodyTA,
        );
      }

      if (this.showNet) {
        this.D3Chart.bars(
          this.svgNode,
          this.chartData,
          0.45,
          "RATEN",
          colorNet(this.colorTheme),
          "rgba(0,0,0,0)",
          "date",
          this.fNames["net"],
          true,
          false,
          false,
          4,
          2,
          true,
          "date",
          false,
          "",
          0,
          false,
          0,
          this.chartBodyTA,
        );
      }
      if (this.showAve) {
        if (this.showLFwd) {
          this.D3Chart.bars(
            this.svgNode,
            this.derivativeAveragesFiltered,
            0.425,
            "DLF",
            colorFwd,
            "rgba(0,0,0,0)",
            "date",
            "lFwd",
            true,
            false,
            false,
            4,
            2,
            true,
            "date",
            false,
            "",
            0,
            false,
            0,
            this.chartBodyTA,
          );
        }
        if (this.showSFwd) {
          this.D3Chart.bars(
            this.svgNode,
            this.derivativeAveragesFiltered,
            0.4,
            "DSF",
            darkFwd,
            "rgba(0,0,0,0)",
            "date",
            "sFwd",
            true,
            false,
            false,
            4,
            2,
            true,
            "date",
            false,
            "",
            0,
            false,
            0,
            this.chartBodyTA,
          );
        }
        if (this.showLCall) {
          this.D3Chart.bars(
            this.svgNode,
            this.derivativeAveragesFiltered,
            0.4,
            "DLC",
            colorCall,
            "rgba(0,0,0,0)",
            "date",
            "lCall",
            true,
            false,
            false,
            4,
            2,
            true,
            "date",
            false,
            "",
            0,
            false,
            0,
            this.chartBodyTA,
          );
          // lCall KO
          this.D3Chart.bars(
            this.svgNode,
            this.derivativeAveragesFiltered,
            0.25,
            "DLCKO",
            colorCall,
            "rgba(0,0,0,0)",
            "date",
            "lCallKO",
            true,
            false,
            false,
            4,
            2,
            true,
            "date",
            false,
            "",
            0,
            false,
            0,
            this.chartBodyTA,
          );
        }
        if (this.showSCall) {
          this.D3Chart.bars(
            this.svgNode,
            this.derivativeAveragesFiltered,
            0.375,
            "DSC",
            darkCall,
            "rgba(0,0,0,0)",
            "date",
            "sCall",
            true,
            false,
            false,
            4,
            2,
            true,
            "date",
            false,
            "",
            0,
            false,
            0,
            this.chartBodyTA,
          );
          // sCall KO
          this.D3Chart.bars(
            this.svgNode,
            this.derivativeAveragesFiltered,
            0.225,
            "DSCKO",
            darkCall,
            "rgba(0,0,0,0)",
            "date",
            "sCallKO",
            true,
            false,
            false,
            4,
            2,
            true,
            "date",
            false,
            "",
            0,
            false,
            0,
            this.chartBodyTA,
          );
        }
        if (this.showLPut) {
          this.D3Chart.bars(
            this.svgNode,
            this.derivativeAveragesFiltered,
            0.375,
            "DLP",
            colorPut,
            "rgba(0,0,0,0)",
            "date",
            "lPut",
            true,
            false,
            false,
            4,
            2,
            true,
            "date",
            false,
            "",
            0,
            false,
            0,
            this.chartBodyTA,
          );
          // sPut KO
          this.D3Chart.bars(
            this.svgNode,
            this.derivativeAveragesFiltered,
            0.2,
            "DLPKO",
            colorPut,
            "rgba(0,0,0,0)",
            "date",
            "lPutKO",
            true,
            false,
            false,
            4,
            2,
            true,
            "date",
            false,
            "",
            0,
            false,
            0,
            this.chartBodyTA,
          );
        }
        if (this.showSPut) {
          this.D3Chart.bars(
            this.svgNode,
            this.derivativeAveragesFiltered,
            0.35,
            "DSP",
            darkPut,
            "rgba(0,0,0,0)",
            "date",
            "sPut",
            true,
            false,
            false,
            4,
            2,
            true,
            "date",
            false,
            "",
            0,
            false,
            0,
            this.chartBodyTA,
          );
          // sPut KO
          this.D3Chart.bars(
            this.svgNode,
            this.derivativeAveragesFiltered,
            0.175,
            "DSPKO",
            darkPut,
            "rgba(0,0,0,0)",
            "date",
            "sPutKO",
            true,
            false,
            false,
            4,
            2,
            true,
            "date",
            false,
            "",
            0,
            false,
            0,
            this.chartBodyTA,
          );
        }
      }

      const barHoverConfiguration = [
        {
          showFields: [
            "showExpo",
            "showMkt",
            "showCommited",
            "showHedge",
            "showNet",
          ],
          data: this.chartData,
          customNameBars: [
            "RATEB",
            "RATEE",
            "RATEC",
            "RATEH",
            "RATEP",
            "RATEN",
          ],
          yFields: [
            this.fNames["budget"],
            this.fNames["market"],
            this.fNames["comm"],
            this.fNames["hedge"],
            this.fNames["protected"],
            this.fNames["net"],
          ],
          multipleYField: "normalizeQty",
          isElementZero: false,
        },
      ];

      const barSimulationsHoverConfiguration = [
        {
          showFields: ["showMkt", "showHedge", "showNet"],
          data: this.simulations,
          customNameBars: ["RATEES", "RATEHS", "RATENS"],
          yFields: [
            { high: "m2m_rate_up", low: "m2m_rate_dn" },
            { high: "hedge_rate_up", low: "hedge_rate_dn" },
            { high: "net_rate_up", low: "net_rate_dn" },
          ],
          multipleYField: "normalizeQty",
          isElementZero: true,
        },
      ];

      const barSimulationHoverConfiguration = [
        {
          showFields: [
            "showLFwd",
            "showSFwd",
            "showLCall",
            "showLCall",
            "showSCall",
            "showSCall",
            "showLPut",
            "showLPut",
            "showSPut",
            "showSPut",
          ],
          data: this.derivativeAveragesFiltered,
          customNameBars: [
            "DLF",
            "DSF",
            "DLC",
            "DLCKO",
            "DSC",
            "DSCKO",
            "DLP",
            "DLPKO",
            "DSP",
            "DSPKO",
          ],
          yFields: [
            "lFwd",
            "sFwd",
            "lCall",
            "lCallKO",
            "sCall",
            "sCallKO",
            "lPut",
            "lPutKO",
            "sPut",
            "sPutKO",
          ],
          multipleYField: "normalizeQty",
          isElementZero: false,
        },
      ];

      const dataLFwd = this.hedgeData.filter(
        (d) =>
          (d.buy_sell === "long" || d.buy_sell === "offset short") &&
          (d.derivative === "forward" || d.derivative === "future"),
      );

      const dataSFwd = this.hedgeData.filter(
        (d) =>
          (d.buy_sell === "short" || d.buy_sell === "offset long") &&
          (d.derivative === "forward" || d.derivative === "future"),
      );

      const dataLCall = this.hedgeData.filter(
        (d) =>
          (d.buy_sell === "long" || d.buy_sell === "offset short") &&
          d.derivative === "call",
      );

      const dataSCall = this.hedgeData.filter(
        (d) =>
          (d.buy_sell === "short" || d.buy_sell === "offset long") &&
          d.derivative === "call",
      );

      const dataLPut = this.hedgeData.filter(
        (d) =>
          (d.buy_sell === "long" || d.buy_sell === "offset short") &&
          d.derivative === "put",
      );

      const dataSPut = this.hedgeData.filter(
        (d) =>
          (d.buy_sell === "short" || d.buy_sell === "offset long") &&
          d.derivative === "put",
      );

      const showFields = [
        "showLFwd",
        "showSFwd",
        "showLCall",
        "showSCall",
        "showLPut",
        "showSPut",
      ];
      const customName = [
        "longFwds",
        "shortFwds",
        "longCalls",
        "shortCalls",
        "longPuts",
        "shortPuts",
      ];
      const twoScattersHoverConfiguration = [
        {
          showFields,
          customName,
          dataHv: [
            dataLFwd,
            dataSFwd,
            dataLCall,
            dataSCall,
            dataLPut,
            dataSPut,
          ],
        },
      ];

      const barrierData = this.hedgeData.filter((d) =>
        isBarrier(d.derivative_type),
      );

      figuresElemsHover.push({ type: "barsOld", data: barHoverConfiguration });

      if (this.showAve) {
        figuresElemsHover.push({
          type: "barsOld",
          data: barSimulationHoverConfiguration,
        });
      }

      if (!this.showAve) {
        figuresElemsHover.push({
          type: "twoScattersOld",
          data: twoScattersHoverConfiguration,
        });
      }

      if (this.showSimulations) {
        figuresElemsHover.push({
          type: "barsOld",
          data: barSimulationsHoverConfiguration,
        });
      }

      if (this.simulations.length > 0 && this.showSimulations) {
        figuresElemsHover.push({
          type: "lineZeroY",
        });
      }

      if (barrierData.length > 0) {
        const scatterbarries = {
          barrierData,
          dataSticks: [
            {
              showFields,
              customName,
              customNameDash: [
                "LFWD",
                "SFWD",
                "LCALL",
                "SCALL",
                "LPUT",
                "SPUT",
              ],
            },
          ],
          dataRect: [{ showFields, customName }],
          xRange,
          dateFld: this.derivativeDateField,
        };
        figuresElemsHover.push({
          type: "scatterBarriersOld",
          data: scatterbarries,
        });
      }

      // === hover fx ===
      this.D3HoverPlanning.hoverFct(
        this.svgNode,
        this.chartData,
        "RATES",
        formatNumber,
        this.currency,
        this.currencyFormat1,
        this.dateFormat,
        this.currencyFormat,
        this.budgetRate,
        formatPercent,
        this.showTooltip,
        formatM,
        this.averages,
        this.showAve,
        this.asset,
        this.isFuture,
        this.colorTheme,
        true,
        this,
        "date",
        this.isZoomY,
        figuresElemsHover,
        "",
        this.chartData,
        false,
        [],
        true,
        true,
      );
      // === create derivatives charts ===
      if (
        this.historicalData.length > 0 &&
        !this.showAve &&
        (this.showLFwd ||
          this.showSFwd ||
          this.showLCall ||
          this.showSCall ||
          this.showLPut ||
          this.showSPut)
      ) {
        let dates = this.historicalData.map((d) => +d.date);
        this.hedgeData.forEach(function(d) {
          d.historicalIndex = idxOfDate(dates, +d.trade_date);
        });
      } else {
        this.hedgeData.forEach(function(d) {
          d.historicalIndex = -1;
        });
      }

      // lFwd
      if (!this.showAve) {
        this.chartBodyScatter = this.svgNode
          .append("g")
          .attr("class", "chartBodyScatter")
          .attr("clip-path", `url(#clipScatter${this.chartName})`);
        this.svgNode
          .append("defs")
          .append("clipPath")
          .attr("id", "clipScatter" + this.chartName)
          .append("rect")
          .attr("width", this.svgNode.width)
          .attr("height", this.svgNode.height);
        if (this.showLFwd) {
          this.D3Chart.scatterImg(
            this.svgNode,
            dataLFwd,
            "longFwds",
            "LFWD",
            formatNumber,
            this.currencyFormat,
            "%d %b %y",
            this.currency,
            this.lFwd,
            this.lFwdOff,
            xRange,
            this.derivativeSize,
            this.lFwd,
            this.sFwd,
            this.lCall,
            this.sCall,
            this.lPut,
            this.sPut,
            this.lFwdOn,
            this.sFwdOn,
            this.lCallOn,
            this.sCallOn,
            this.lPutOn,
            this.sPutOn,
            this.lFwdOff,
            this.sFwdOff,
            this.lCallOff,
            this.sCallOff,
            this.lPutOff,
            this.sPutOff,
            "longFwds",
            "shortFwds",
            "longCalls",
            "shortCalls",
            "longPuts",
            "shortPuts",
            this.showTooltipDeriv,
            this.candleData.length === 0,
            this.asset,
            this.adjustTooltip,
            this.colorTheme,
            this.chartBodyScatter,
            true,
            this.derivativeDateField,
          );
        }
        // sFwd
        if (this.showSFwd) {
          this.D3Chart.scatterImg(
            this.svgNode,
            dataSFwd,
            "shortFwds",
            "SFWD",
            formatNumber,
            this.currencyFormat,
            "%d %b %y",
            this.currency,
            this.sFwd,
            this.sFwdOff,
            xRange,
            this.derivativeSize,
            this.lFwd,
            this.sFwd,
            this.lCall,
            this.sCall,
            this.lPut,
            this.sPut,
            this.lFwdOn,
            this.sFwdOn,
            this.lCallOn,
            this.sCallOn,
            this.lPutOn,
            this.sPutOn,
            this.lFwdOff,
            this.sFwdOff,
            this.lCallOff,
            this.sCallOff,
            this.lPutOff,
            this.sPutOff,
            "longFwds",
            "shortFwds",
            "longCalls",
            "shortCalls",
            "longPuts",
            "shortPuts",
            this.showTooltipDeriv,
            this.candleData.length === 0,
            this.asset,
            this.adjustTooltip,
            this.colorTheme,
            this.chartBodyScatter,
            true,
            this.derivativeDateField,
          );
        }
        // lCall
        if (this.showLCall) {
          this.D3Chart.scatterImg(
            this.svgNode,
            dataLCall,
            "longCalls",
            "LCALL",
            formatNumber,
            this.currencyFormat,
            "%d %b %y",
            this.currency,
            this.lCall,
            this.lCallOff,
            xRange,
            this.derivativeSize,
            this.lFwd,
            this.sFwd,
            this.lCall,
            this.sCall,
            this.lPut,
            this.sPut,
            this.lFwdOn,
            this.sFwdOn,
            this.lCallOn,
            this.sCallOn,
            this.lPutOn,
            this.sPutOn,
            this.lFwdOff,
            this.sFwdOff,
            this.lCallOff,
            this.sCallOff,
            this.lPutOff,
            this.sPutOff,
            "longFwds",
            "shortFwds",
            "longCalls",
            "shortCalls",
            "longPuts",
            "shortPuts",
            this.showTooltipDeriv,
            this.candleData.length === 0,
            this.asset,
            this.adjustTooltip,
            this.colorTheme,
            this.chartBodyScatter,
            true,
            this.derivativeDateField,
          );
        }
        // sCall
        if (this.showSCall) {
          this.D3Chart.scatterImg(
            this.svgNode,
            dataSCall,
            "shortCalls",
            "SCALL",
            formatNumber,
            this.currencyFormat,
            "%d %b %y",
            this.currency,
            this.sCall,
            this.sCallOff,
            xRange,
            this.derivativeSize,
            this.lFwd,
            this.sFwd,
            this.lCall,
            this.sCall,
            this.lPut,
            this.sPut,
            this.lFwdOn,
            this.sFwdOn,
            this.lCallOn,
            this.sCallOn,
            this.lPutOn,
            this.sPutOn,
            this.lFwdOff,
            this.sFwdOff,
            this.lCallOff,
            this.sCallOff,
            this.lPutOff,
            this.sPutOff,
            "longFwds",
            "shortFwds",
            "longCalls",
            "shortCalls",
            "longPuts",
            "shortPuts",
            this.showTooltipDeriv,
            this.candleData.length === 0,
            this.asset,
            this.adjustTooltip,
            this.colorTheme,
            this.chartBodyScatter,
            true,
            this.derivativeDateField,
          );
        }
        // lPut
        if (this.showLPut) {
          this.D3Chart.scatterImg(
            this.svgNode,
            dataLPut,
            "longPuts",
            "LPUT",
            formatNumber,
            this.currencyFormat,
            "%d %b %y",
            this.currency,
            this.lPut,
            this.lPutOff,
            xRange,
            this.derivativeSize,
            this.lFwd,
            this.sFwd,
            this.lCall,
            this.sCall,
            this.lPut,
            this.sPut,
            this.lFwdOn,
            this.sFwdOn,
            this.lCallOn,
            this.sCallOn,
            this.lPutOn,
            this.sPutOn,
            this.lFwdOff,
            this.sFwdOff,
            this.lCallOff,
            this.sCallOff,
            this.lPutOff,
            this.sPutOff,
            "longFwds",
            "shortFwds",
            "longCalls",
            "shortCalls",
            "longPuts",
            "shortPuts",
            this.showTooltipDeriv,
            this.candleData.length === 0,
            this.asset,
            this.adjustTooltip,
            this.colorTheme,
            this.chartBodyScatter,
            true,
            this.derivativeDateField,
          );
        }
        // sPut
        if (this.showSPut) {
          this.D3Chart.scatterImg(
            this.svgNode,
            dataSPut,
            "shortPuts",
            "SPUT",
            formatNumber,
            this.currencyFormat,
            "%d %b %y",
            this.currency,
            this.sPut,
            this.sPutOff,
            xRange,
            this.derivativeSize,
            this.lFwd,
            this.sFwd,
            this.lCall,
            this.sCall,
            this.lPut,
            this.sPut,
            this.lFwdOn,
            this.sFwdOn,
            this.lCallOn,
            this.sCallOn,
            this.lPutOn,
            this.sPutOn,
            this.lFwdOff,
            this.sFwdOff,
            this.lCallOff,
            this.sCallOff,
            this.lPutOff,
            this.sPutOff,
            "longFwds",
            "shortFwds",
            "longCalls",
            "shortCalls",
            "longPuts",
            "shortPuts",
            this.showTooltipDeriv,
            this.candleData.length === 0,
            this.asset,
            this.adjustTooltip,
            this.colorTheme,
            this.chartBodyScatter,
            true,
            this.derivativeDateField,
          );
        }
      }
    },
    onHoverChart() {
      this.$emit("onTableFormat", this.formatVars.userHovered);
    },
    callWidth() {
      if (this.chartData.length > 0) {
        this.resizeDerivatives();
        this.createBarChart();
      }
    },
  },
};
</script>

<style scoped></style>
